import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Play,
  Square,
  Activity,
  ArrowDownCircle,
  ArrowUpCircle,
  Clock,
  Wifi,
  Globe,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  smartoltService,
  LiveTrafficTestSession,
  LiveTrafficSample,
} from "../../services/smartolt.service";

interface LiveTrafficTestConsoleProps {
  customerId?: string;
  onuExternalId?: string;
  targetLabel?: string;
  compact?: boolean;
}

interface ChartPoint {
  time: string;
  elapsed: number;
  down: number;
  up: number;
}

export const LiveTrafficTestConsole: React.FC<LiveTrafficTestConsoleProps> = ({
  customerId,
  onuExternalId,
  targetLabel,
  compact = false,
}) => {
  const [session, setSession] = useState<LiveTrafficTestSession | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sample, setSample] = useState<LiveTrafficSample | null>(null);
  const [history, setHistory] = useState<ChartPoint[]>([]);
  const [completedSummary, setCompletedSummary] = useState<LiveTrafficSample | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const activeSessionIdRef = useRef<string | null>(null);

  const stopTest = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const sid = activeSessionIdRef.current;
    if (sid) {
      try {
        const finalSample = await smartoltService.stopLiveTrafficTest(sid);
        setSample(finalSample);
        setCompletedSummary(finalSample);
      } catch (err) {
        console.warn("Error stopping traffic test:", err);
      }
    }
    setIsRunning(false);
    activeSessionIdRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (activeSessionIdRef.current) {
        try {
          const p = smartoltService.stopLiveTrafficTest(activeSessionIdRef.current);
          if (p && typeof (p as any).catch === "function") {
            (p as any).catch(() => {});
          }
        } catch {
          // ignore unmount errors
        }
      }
    };
  }, []);

  const startTest = async () => {
    if (!customerId && !onuExternalId) {
      toast.error("Target customer or ONU external ID is required");
      return;
    }

    setLoading(true);
    setHistory([]);
    setCompletedSummary(null);
    setSample(null);

    try {
      const sess = await smartoltService.startLiveTrafficTest({
        customerId,
        onuExternalId,
        durationSeconds: 30,
      });

      setSession(sess);
      activeSessionIdRef.current = sess.session_id;
      setIsRunning(true);
      toast.info("Live traffic test initiated (30-second session)");

      // Initial sample fetch
      const s0 = await smartoltService.getLiveTrafficSample(sess.session_id);
      setSample(s0);
      setHistory([{ time: "0s", elapsed: s0.elapsed_seconds || 0, down: s0.download_mbps, up: s0.upload_mbps }]);

      // Start 2-second interval
      timerRef.current = setInterval(async () => {
        const sid = activeSessionIdRef.current;
        if (!sid) return;

        try {
          const s = await smartoltService.getLiveTrafficSample(sid);
          setSample(s);
          setHistory((prev) => [
            ...prev.slice(-14),
            {
              time: `${Math.round(s.elapsed_seconds)}s`,
              elapsed: s.elapsed_seconds,
              down: s.download_mbps,
              up: s.upload_mbps,
            },
          ]);

          if (!s.is_active || s.remaining_seconds <= 0 || s.completed) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setIsRunning(false);
            activeSessionIdRef.current = null;
            setCompletedSummary(s);
            toast.success("Live traffic test completed successfully");
          }
        } catch (sampleErr) {
          console.error("Failed to fetch traffic sample:", sampleErr);
          stopTest();
        }
      }, 2000);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to start live traffic test");
      setIsRunning(false);
      activeSessionIdRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  // Speedometer calculation
  const maxScale = Math.max(
    50,
    sample?.download_peak_mbps ? Math.ceil((sample.download_peak_mbps * 1.2) / 25) * 25 : 50
  );

  const downRate = sample?.download_mbps ?? 0;
  const upRate = sample?.upload_mbps ?? 0;
  const downAngle = Math.min(180, Math.max(0, (downRate / maxScale) * 180));
  const upAngle = Math.min(180, Math.max(0, (upRate / (maxScale / 2)) * 180));

  // Remaining time and progress
  const elapsed = sample?.elapsed_seconds ?? 0;
  const remaining = sample?.remaining_seconds ?? 30;
  const progressPercent = Math.min(100, (elapsed / 30) * 100);

  // SVG Chart Dimensions & Padding for Professional Axis Labels
  const chartW = compact ? 360 : 520;
  const chartH = compact ? 130 : 160;
  const paddingLeft = compact ? 48 : 56;
  const paddingRight = compact ? 14 : 20;
  const paddingTop = 20;
  const paddingBottom = 26;

  const plotX = paddingLeft;
  const plotY = paddingTop;
  const plotW = chartW - paddingLeft - paddingRight;
  const plotH = chartH - paddingTop - paddingBottom;
  const baselineY = plotY + plotH;
  const totalDuration = session?.duration_seconds || 30;

  // Compute clean Y-axis scale and tick values
  const highestObserved = history.length > 0
    ? Math.max(...history.map((h) => Math.max(h.down, h.up)))
    : 10;
  const rawMax = Math.max(10, highestObserved * 1.15);
  const step = Math.max(5, Math.ceil(rawMax / 3 / 5) * 5);
  const maxChartVal = step * 3;
  const yTicks = [maxChartVal, step * 2, step, 0];

  // Compute X-axis time ticks across duration
  const xTicks = compact ? [0, 10, 20, 30] : [0, 5, 10, 15, 20, 25, 30];

  const downPointsArr = history.map((h) => {
    const x = plotX + Math.min(1, Math.max(0, h.elapsed / totalDuration)) * plotW;
    const y = baselineY - Math.min(1, Math.max(0, h.down / maxChartVal)) * plotH;
    return { x, y, val: h.down, elapsed: h.elapsed };
  });

  const upPointsArr = history.map((h) => {
    const x = plotX + Math.min(1, Math.max(0, h.elapsed / totalDuration)) * plotW;
    const y = baselineY - Math.min(1, Math.max(0, h.up / maxChartVal)) * plotH;
    return { x, y, val: h.up, elapsed: h.elapsed };
  });

  const downPoints = downPointsArr.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const upPoints = upPointsArr.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const firstX = downPointsArr.length > 0 ? downPointsArr[0].x.toFixed(1) : plotX.toFixed(1);
  const lastX = downPointsArr.length > 0 ? downPointsArr[downPointsArr.length - 1].x.toFixed(1) : plotX.toFixed(1);
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 shadow-xs space-y-4">
      {/* 1. Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="flex h-2.5 w-2.5 relative">
              {isRunning ? (
                <>
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </>
              ) : (
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-slate-400" />
              )}
            </span>
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <Activity className="h-4 w-4 text-blue-600" />
              On-Demand Live Traffic Test
            </h4>
            {isRunning && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-200 animate-pulse">
                Live Active
              </span>
            )}
            {completedSummary && !isRunning && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 border border-blue-200">
                Completed
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            {targetLabel ? `Target: ${targetLabel} · ` : ""}
            Per-second subscriber throughput & latency diagnostic.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {!isRunning ? (
            <button
              type="button"
              onClick={startTest}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-emerald-700 active:scale-98 transition-all disabled:opacity-50"
            >
              <Play className="h-3.5 w-3.5 fill-current" />
              {completedSummary ? "Run Test Again" : "Run Live Traffic Test"}
            </button>
          ) : (
            <button
              type="button"
              onClick={stopTest}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-rose-700 active:scale-98 transition-all"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
              Stop Test ({Math.round(remaining)}s)
            </button>
          )}
        </div>
      </div>

      {/* 2. Active Session Progress Bar */}
      {isRunning && (
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] font-medium text-slate-600">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-slate-400" />
              Elapsed: {Math.round(elapsed)}s / 30s
            </span>
            <span className="text-emerald-700 font-semibold font-mono">
              {Math.round(remaining)}s remaining (Auto-terminates)
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 border border-slate-200">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-1000 ease-linear shadow-xs"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* 3. Live Dashboard Content */}
      {isRunning || sample || completedSummary ? (
        <div className="space-y-4">
          {/* Dual Speed Gauges Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Download Card */}
            <div className="rounded-xl border border-blue-200/80 bg-blue-50/50 p-3.5 flex flex-col justify-between relative overflow-hidden">
              <div className="flex items-center justify-between pb-2 border-b border-blue-100">
                <span className="flex items-center gap-1.5 text-xs font-bold text-blue-900">
                  <ArrowDownCircle className="h-4 w-4 text-blue-600" />
                  Download Rate
                </span>
                <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider bg-blue-100 border border-blue-200 px-2 py-0.5 rounded-full">
                  Live Throughput
                </span>
              </div>

              <div className="py-3 flex items-baseline justify-center gap-1">
                <span className="text-3xl sm:text-4xl font-black font-mono tracking-tight text-blue-900">
                  {downRate.toFixed(1)}
                </span>
                <span className="text-xs font-bold text-blue-600 uppercase">Mbps</span>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-blue-100 text-[11px]">
                <div className="text-slate-600">
                  Peak: <strong className="text-blue-900 font-mono">{(sample?.download_peak_mbps ?? 0).toFixed(1)} Mbps</strong>
                </div>
                <div className="text-slate-600 text-right">
                  Avg: <strong className="text-blue-900 font-mono">{(sample?.download_avg_mbps ?? 0).toFixed(1)} Mbps</strong>
                </div>
              </div>
            </div>

            {/* Upload Card */}
            <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-3.5 flex flex-col justify-between relative overflow-hidden">
              <div className="flex items-center justify-between pb-2 border-b border-amber-100">
                <span className="flex items-center gap-1.5 text-xs font-bold text-amber-900">
                  <ArrowUpCircle className="h-4 w-4 text-amber-600" />
                  Upload Rate
                </span>
                <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                  Live Throughput
                </span>
              </div>

              <div className="py-3 flex items-baseline justify-center gap-1">
                <span className="text-3xl sm:text-4xl font-black font-mono tracking-tight text-amber-900">
                  {upRate.toFixed(1)}
                </span>
                <span className="text-xs font-bold text-amber-600 uppercase">Mbps</span>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-amber-100 text-[11px]">
                <div className="text-slate-600">
                  Peak: <strong className="text-amber-900 font-mono">{(sample?.upload_peak_mbps ?? 0).toFixed(1)} Mbps</strong>
                </div>
                <div className="text-slate-600 text-right">
                  Avg: <strong className="text-amber-900 font-mono">{(sample?.upload_avg_mbps ?? 0).toFixed(1)} Mbps</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Real-time Streaming Polyline Chart */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
              <span className="flex items-center gap-1">
                <Activity className="h-3.5 w-3.5 text-blue-600" />
                Real-Time Bandwidth Waveform (Last 15 Samples)
              </span>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="flex items-center gap-1 text-blue-600 font-medium">
                  <span className="h-2 w-2 rounded-full bg-blue-600" /> Download
                </span>
                <span className="flex items-center gap-1 text-amber-600 font-medium">
                  <span className="h-2 w-2 rounded-full bg-amber-500" /> Upload
                </span>
              </div>
            </div>
            <div className="w-full overflow-hidden flex justify-center">
              <svg
                viewBox={`0 0 ${chartW} ${chartH}`}
                className="w-full h-auto max-h-44 overflow-visible"
              >
                <defs>
                  <linearGradient id="downGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
                  </linearGradient>
                  <linearGradient id="upGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.20" />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.0" />
                  </linearGradient>
                </defs>

                {/* Y-Axis Speed Title */}
                <text
                  x={plotX}
                  y={plotY - 7}
                  textAnchor="start"
                  fill="#64748B"
                  fontSize="9"
                  fontFamily="ui-sans-serif, system-ui, sans-serif"
                  fontWeight="700"
                  letterSpacing="0.05em"
                >
                  SPEED (Mbps)
                </text>

                {/* Y-Axis Gridlines & Speed Labels */}
                {yTicks.map((tickVal, i) => {
                  const y = plotY + (i / (yTicks.length - 1)) * plotH;
                  return (
                    <g key={tickVal}>
                      <line
                        x1={plotX}
                        y1={y}
                        x2={plotX + plotW}
                        y2={y}
                        stroke="#E2E8F0"
                        strokeWidth="1"
                        strokeDasharray={tickVal === 0 ? undefined : "3 3"}
                      />
                      <text
                        x={plotX - 8}
                        y={y}
                        textAnchor="end"
                        dominantBaseline="central"
                        fill="#64748B"
                        fontSize="10"
                        fontFamily="ui-monospace, monospace"
                        fontWeight="500"
                      >
                        {tickVal} {compact ? "M" : "Mbps"}
                      </text>
                    </g>
                  );
                })}

                {/* X-Axis Baseline */}
                <line
                  x1={plotX}
                  y1={baselineY}
                  x2={plotX + plotW}
                  y2={baselineY}
                  stroke="#CBD5E1"
                  strokeWidth="1.2"
                />

                {/* X-Axis Time Ticks & Labels */}
                {xTicks.map((t) => {
                  const x = plotX + (t / totalDuration) * plotW;
                  return (
                    <g key={t}>
                      <line
                        x1={x}
                        y1={baselineY}
                        x2={x}
                        y2={baselineY + 4}
                        stroke="#94A3B8"
                        strokeWidth="1"
                      />
                      <text
                        x={x}
                        y={baselineY + 16}
                        textAnchor="middle"
                        fill="#64748B"
                        fontSize="10"
                        fontFamily="ui-monospace, monospace"
                        fontWeight="500"
                      >
                        {t}s
                      </text>
                    </g>
                  );
                })}

                {/* X-Axis Time Title */}
                <text
                  x={plotX + plotW}
                  y={baselineY + 16}
                  textAnchor="end"
                  fill="#64748B"
                  fontSize="9"
                  fontFamily="ui-sans-serif, system-ui, sans-serif"
                  fontWeight="600"
                  letterSpacing="0.05em"
                >
                  TIME
                </text>

                {/* Shaded Area Fills under Curves */}
                {history.length > 1 && (
                  <>
                    <polygon
                      points={`${firstX},${baselineY} ${downPoints} ${lastX},${baselineY}`}
                      fill="url(#downGrad)"
                    />
                    <polygon
                      points={`${firstX},${baselineY} ${upPoints} ${lastX},${baselineY}`}
                      fill="url(#upGrad)"
                    />
                  </>
                )}

                {/* Download Polyline */}
                {history.length > 1 && (
                  <polyline
                    fill="none"
                    stroke="#2563EB"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={downPoints}
                  />
                )}

                {/* Upload Polyline */}
                {history.length > 1 && (
                  <polyline
                    fill="none"
                    stroke="#D97706"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={upPoints}
                  />
                )}

                {/* Data Points on Download with Tooltip */}
                {downPointsArr.map((p, i) => (
                  <circle
                    key={`d-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r="3"
                    fill="#2563EB"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    className="transition-all hover:scale-125"
                  >
                    <title>{`Download: ${p.val.toFixed(1)} Mbps at ${Math.round(p.elapsed)}s`}</title>
                  </circle>
                ))}

                {/* Data Points on Upload with Tooltip */}
                {upPointsArr.map((p, i) => (
                  <circle
                    key={`u-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r="2.5"
                    fill="#D97706"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    className="transition-all hover:scale-125"
                  >
                    <title>{`Upload: ${p.val.toFixed(1)} Mbps at ${Math.round(p.elapsed)}s`}</title>
                  </circle>
                ))}
              </svg>
            </div>
          </div>

          {/* Network & Diagnostics Telemetry Strip */}
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
              <span className="block text-[10px] uppercase font-semibold text-slate-500">RTT Latency</span>
              <span className="font-mono font-bold text-slate-800">
                {sample?.latency_ms ? `${sample.latency_ms} ms` : "—"}
              </span>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
              <span className="block text-[10px] uppercase font-semibold text-slate-500">Total Transferred</span>
              <span className="font-mono font-bold text-slate-800">
                {sample?.total_bytes_transferred
                  ? `${(sample.total_bytes_transferred / (1024 * 1024)).toFixed(1)} MB`
                  : "0.0 MB"}
              </span>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
              <span className="block text-[10px] uppercase font-semibold text-slate-500">Subscriber IP</span>
              <span className="font-mono font-bold text-slate-800 truncate block">
                {sample?.wan_ip || "100.82.x.x"}
              </span>
            </div>
          </div>

          {/* Completed Summary Banner */}
          {completedSummary && !isRunning && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="space-y-1 text-xs">
                <h5 className="font-bold text-emerald-900">Test Session Completed</h5>
                <p className="text-emerald-800">
                  Peak speeds reached{" "}
                  <strong className="text-emerald-900 font-semibold">{completedSummary.download_peak_mbps.toFixed(1)} Mbps ↓</strong> and{" "}
                  <strong className="text-emerald-900 font-semibold">{completedSummary.upload_peak_mbps.toFixed(1)} Mbps ↑</strong> over{" "}
                  {Math.round(completedSummary.elapsed_seconds)} seconds. Link is stable with{" "}
                  {completedSummary.latency_ms} ms round-trip time.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Idle Prompt State */
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center space-y-3">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600 border border-blue-200">
            <Wifi className="h-5 w-5" />
          </div>
          <div className="space-y-1 max-w-sm mx-auto">
            <h5 className="text-xs font-bold text-slate-800">Ready to Measure Live Traffic</h5>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Click &quot;Run Live Traffic Test&quot; above to initiate a 30-second live diagnostic session.
              Measures instantaneous download/upload bitrates, latency jitter, and burst peaks.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
